<template>
  <section class="lola-cta-section">
    <div class="cta-card">
      <div class="cta-card__header">
        <div class="cta-card__avatar">
          <NuxtImg
            src="/images/lola-avatar.png"
            alt="Lola"
            class="cta-card__avatar-img"
            width="72"
            height="72"
            loading="lazy"
            decoding="async"
            format="webp" />
        </div>
        <div class="cta-card__content">
          <h3 class="cta-card__title">{{ titleValue }}</h3>
          <p class="cta-card__desc">{{ descriptionValue }}</p>
        </div>
      </div>

      <ul
        v-if="checksValue.length"
        class="cta-card__checks">
        <li
          v-for="(check, index) in checksValue"
          :key="index"
          class="cta-card__check">
          <span
            class="cta-card__check-icon"
            aria-hidden="true"
            >✓</span
          >
          <span>{{ check }}</span>
        </li>
      </ul>

      <NuxtLink
        :to="localePath(props.to ?? (isLoggedIn ? '/discuter' : '/register'))"
        class="cta-card__btn"
        @click="onCtaClick">
        {{ buttonLabelValue }}
      </NuxtLink>

      <p
        v-if="footerValue"
        class="cta-card__footer">
        {{ footerValue }}
      </p>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import type { ComputedRef } from 'vue'
import type { PrePeersLolaCtaProps, PrePeersLolaCtaVariant } from '~/types/PrePeersLolaCta'
import { TRACKING_EVENTS } from '~/constants/trackingEvents'

/**
 * Définit les props du composant PrePeersLolaCta.
 *
 * - `variant` choisit les textes i18n par défaut (métier, école, programme, blog, pages institutionnelles)
 * - chaque texte reste overridable via prop dédiée (title, description, buttonLabel, checks, footer)
 * - `entityName` est potentiellement injecté dans le titre et la description
 */
const props = defineProps({
  variant: {
    type: String as () => PrePeersLolaCtaVariant,
    default: 'metier',
    validator: (value: string): boolean =>
      ['metier', 'ecole', 'programme', 'blog', 'institutional'].includes(value),
  },
  entityName: {
    type: String,
    default: '',
  },
  title: {
    type: String,
    default: undefined,
  },
  description: {
    type: String,
    default: undefined,
  },
  buttonLabel: {
    type: String,
    default: undefined,
  },
  checks: {
    type: Array as () => string[],
    default: undefined,
  },
  footer: {
    type: String,
    default: undefined,
  },
  showFooter: {
    type: Boolean,
    default: true,
  },
  to: {
    type: String,
    default: undefined,
  },
}) as PrePeersLolaCtaProps

const { isLoggedIn } = useAuth()
const { track } = useTracking()
const route = useRoute()
const { t, tm, rt } = useI18n()

const checksValue: ComputedRef<string[]> = computed((): string[] => {
  if (props.checks !== undefined) return props.checks
  const messages = tm(i18nKey('checks')) as unknown
  if (!Array.isArray(messages)) return []
  return messages.map((m) => rt(m))
})

const localePath = useLocalePath()

const i18nKey = (suffix: string): string => `lolaCta.${props.variant}.${suffix}`

const titleValue: ComputedRef<string> = computed(
  (): string => props.title ?? t(i18nKey('title'), { entity: props.entityName ?? '' }),
)

const descriptionValue: ComputedRef<string> = computed(
  (): string => props.description ?? t(i18nKey('description'), { entity: props.entityName ?? '' }),
)

const buttonLabelValue: ComputedRef<string> = computed(
  (): string => props.buttonLabel ?? t(i18nKey('buttonLabel'), { entity: props.entityName ?? '' }),
)

const footerValue: ComputedRef<string> = computed((): string => {
  if (!props.showFooter) return ''
  return props.footer ?? t(i18nKey('footer'))
})

/**
 * Trace le clic sur le CTA Lola (pont SEO → funnel) sans bloquer la navigation.
 * @returns {void}
 */
function onCtaClick(): void {
  track(TRACKING_EVENTS.CTA_LOLA_CLICKED, {
    variant: props.variant,
    entityName: props.entityName,
    location: route.path,
  })
}
</script>

<style scoped>
.lola-cta-section {
  margin: var(--space-10) 0;
}

.cta-card {
  position: relative;
  max-width: 1024px;
  overflow: hidden;
}

.cta-card__header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--space-3);
  margin-bottom: var(--space-5);
}

.cta-card__avatar {
  width: auto;
  height: auto;
  flex-shrink: 0;
  overflow: visible;
}

/* Ne pas recadrer en cercle : la pastille « en ligne » fait partie du PNG (coin bas-droit). */
.cta-card__avatar-img {
  display: block;
  width: 72px;
  height: auto;
}

.cta-card__content {
  width: 100%;
  min-width: 0;
}

.cta-card__title {
  margin: 0 0 var(--space-2) 0;
  color: #ffffff;
  font-size: var(--font-size-xl, 1.5rem);
  font-weight: 700;
  line-height: 1.3;
}

.cta-card__desc {
  margin: 0;
  color: #b8b8c8;
  font-size: var(--font-size-base, 1rem);
  line-height: 1.5;
  margin-top: var(--space-4);
}

.cta-card__checks {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4) var(--space-6);
  margin: 0 0 var(--space-5) 0;
  padding: 0;
  list-style: none;
  justify-content: center;
}

.cta-card__check {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  color: #e0e0eb;
  font-size: var(--font-size-sm, 0.875rem);
}

.cta-card__check-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background: #22c55e;
  color: #0a0a14;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;
}

.cta-card__btn {
  display: block;
  width: fit-content;
  margin: 0 auto var(--space-4) auto;
  padding: var(--space-3) var(--space-6);
  background: linear-gradient(135deg, #4f7cff 0%, #6b5bff 100%);
  color: #ffffff;
  font-weight: 600;
  text-decoration: none;
  border-radius: var(--radius-md, 8px);
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;
}

.cta-card__btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(79, 124, 255, 0.35);
}

.cta-card__footer {
  margin: 0;
  text-align: center;
  color: #8a8a9a;
  font-size: var(--font-size-sm, 0.875rem);
}

@media (max-width: 640px) {
  .cta-card__checks {
    flex-direction: column;
    gap: var(--space-2);
    align-items: center;
  }
}
</style>
